import { api } from "rdx/services/api";
import { actions } from "rdx";
import SchemaValidator from "util/schema-helpers/SchemaValidator";
import { camelizeKeys, keysToSnakeCase } from "lib/helpers/convertObjKeys";
import { VisionClient } from "graphql/visionClient";
import { ORG_PROJECTS_TAGS } from "rdx/modules/enterpriseProjects/tags";
import {
  CreateOverrideResponseT,
  CreateOverrideParamsT,
  GetProjectProposalsParamsT,
  GetProjectProposalsResponseT,
  RenameProposalParamsT,
  RenameProposalResponseT,
  RequestRevisionParamsT,
  RequestRevisionResponseT,
  RequestVisionRevisionParamsT,
  RequestVisionRevisionResponseT,
  LinkAuroraDesignParamsT,
  LinkAuroraDesignResponseT,
  RefreshAuroraDesignParamsT,
  RefreshAuroraDesignResponseT,
  GetProposalDetailsParamsT,
  GetProposalDetailsResponseT,
  GetProjectActionsResponseT,
  GetProjectActionsQueryParamsT,
  CloseProjectParamsT,
  CloseProjectResponseT,
  PreSalesKpiT,
  PreSalesKpiApiResponseT,
  GetPreSalesKpisResponseT,
  ProviderLeadsCountApiResponseT,
} from "./projectTypes";
import {
  CreateOverrideResponseSchema,
  GetProjectProposalsResponseSchema,
  RenameProposalResponseSchema,
  RequestRevisionResponseSchema,
  RequestVisionRevisionResponseSchema,
  LinkAuroraDesignResponseSchema,
  RefreshAuroraDesignResponseSchema,
  GetProposalDetailsResponseSchema,
  GetProjectActionsResponseSchema,
} from "./schemas";
import messages from "./messages";
import { PROJECT_TAGS } from "./tags";

export const providerLeadsApi = api
  .enhanceEndpoints({ addTagTypes: [...Object.values(PROJECT_TAGS), ...Object.values(ORG_PROJECTS_TAGS)] })
  .injectEndpoints({
    endpoints: (builder) => ({
      createPowurlineOverride: builder.mutation<CreateOverrideResponseT["body"], CreateOverrideParamsT>({
        query: ({ projectId, sellerPowurlinePpw }) => ({
          url: `provider_leads/${projectId}/seller_powurline_overrides`,
          method: "POST",
          body: keysToSnakeCase({ sellerPowurlinePpw }),
        }),
        transformResponse: (res: CreateOverrideResponseT) => {
          SchemaValidator.validate<typeof CreateOverrideResponseSchema>({
            schema: CreateOverrideResponseSchema,
            schemaName: "CreateOverrideResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (queryParams, { dispatch, queryFulfilled }) => {
          dispatch(
            actions.setAlertMessageVisible({
              message: "Submitting...",
              severity: "info",
              duration: 6000,
            }),
          );
          queryFulfilled
            .then(() => {
              dispatch(actions.setProjectSellerOverride(queryParams.sellerPowurlinePpw));
              dispatch(actions.setAdminProjectSellerOverride(queryParams.sellerPowurlinePpw));
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_CREATING_SELLER_OVERRIDE,
                  severity: "error",
                }),
              );
            });
        },
      }),
      getProjectProposals: builder.query<GetProjectProposalsResponseT["body"], GetProjectProposalsParamsT>({
        query: ({ id, enterprise }) => ({
          url: `provider_leads/${id}/proposals`,
          params: keysToSnakeCase({ enterprise }),
        }),
        transformResponse: (res: GetProjectProposalsResponseT) => {
          SchemaValidator.validate<typeof GetProjectProposalsResponseSchema>({
            schema: GetProjectProposalsResponseSchema,
            schemaName: "GetProjectProposalsResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        providesTags: [PROJECT_TAGS.PROJECT_PROPOSALS],
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled.catch(() => {
            dispatch(
              actions.setAlertMessageVisible({
                message: messages.ERROR_GETTING_PROPOSALS,
                severity: "error",
              }),
            );
          });
        },
      }),
      renameProposal: builder.mutation<RenameProposalResponseT["body"], RenameProposalParamsT>({
        query: ({ proposalId, projectId, name, isAdmin = false }) => ({
          url: isAdmin
            ? `projects/${projectId}/proposals/${proposalId}/rename`
            : `provider_leads/${projectId}/proposals/${proposalId}/rename`,
          method: "PATCH",
          body: keysToSnakeCase({ name }),
        }),
        transformResponse: (res: RenameProposalResponseT) => {
          SchemaValidator.validate<typeof RenameProposalResponseSchema>({
            schema: RenameProposalResponseSchema,
            schemaName: "RenameProposalResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        invalidatesTags: [PROJECT_TAGS.PROJECT_PROPOSALS],
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.RENAME_PROPOSAL_SUCCESS,
                  severity: "success",
                }),
              );
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_RENAMING_PROPOSAL,
                  severity: "error",
                }),
              );
            });
        },
      }),
      getProposalDetails: builder.query<GetProposalDetailsResponseT["body"], GetProposalDetailsParamsT>({
        query: ({ id, proposalId, isAdmin = false, enterprise }) => ({
          url: isAdmin ? `projects/${id}/proposals/${proposalId}` : `provider_leads/${id}/proposals/${proposalId}`,
          params: enterprise !== undefined ? keysToSnakeCase({ enterprise }) : undefined,
        }),
        transformResponse: (res: GetProposalDetailsResponseT) => {
          SchemaValidator.validate<typeof GetProposalDetailsResponseSchema>({
            schema: GetProposalDetailsResponseSchema,
            schemaName: "GetProposalDetailsResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        providesTags: [PROJECT_TAGS.PROJECT_PROPOSALS],
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled.catch(() => {
            dispatch(
              actions.setAlertMessageVisible({
                message: messages.ERROR_GETTING_PROPOSAL,
                severity: "error",
              }),
            );
          });
        },
      }),
      requestRevision: builder.mutation<RequestRevisionResponseT["body"], RequestRevisionParamsT>({
        query: ({ id, reason, note, project_id }) => ({
          url: `proposals/${id}/revision`,
          method: "POST",
          body: keysToSnakeCase({ reason, note, project_id }),
        }),
        transformResponse: (res: RequestRevisionResponseT) => {
          SchemaValidator.validate<typeof RequestRevisionResponseSchema>({
            schema: RequestRevisionResponseSchema,
            schemaName: "RequestRevisionResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        invalidatesTags: [PROJECT_TAGS.PROJECT_PROPOSALS],
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.REQUEST_REVISION_SUCCESS,
                  severity: "success",
                }),
              );
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_REQUESTING_REVISION,
                  severity: "error",
                }),
              );
            });
        },
      }),
      requestVisionRevision: builder.mutation<
        RequestVisionRevisionResponseT["requestDesignRevision"],
        RequestVisionRevisionParamsT
      >({
        queryFn: async ({ reasons, note, parentId }) => {
          try {
            const visionClient = new VisionClient();
            const response = await visionClient.requestRevision({
              notes: note,
              reasons,
              parentId,
            });

            SchemaValidator.validate<typeof RequestVisionRevisionResponseSchema>({
              schema: RequestVisionRevisionResponseSchema,
              schemaName: "RequestVisionRevisionResponseSchema",
              dto: camelizeKeys(response),
            });

            return { data: camelizeKeys(response.requestDesignRevision) };
          } catch (error) {
            return { error: { status: "CUSTOM_ERROR", error: String(error) } };
          }
        },
        invalidatesTags: [PROJECT_TAGS.PROJECT_PROPOSALS],
        onQueryStarted: ({ projectId }, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(() => {
              dispatch(actions.invalidateProposalCache({ projectId }));
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.REQUEST_VISION_REVISION_SUCCESS,
                  severity: "success",
                }),
              );
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_REQUESTING_VISION_REVISION,
                  severity: "error",
                }),
              );
            });
        },
      }),
      linkAuroraDesign: builder.mutation<LinkAuroraDesignResponseT["body"], LinkAuroraDesignParamsT>({
        query: ({ projectId, proposalId, auroraDesignId }) => ({
          url: `projects/${projectId}/proposals/${proposalId}/link_aurora_design`,
          method: "POST",
          body: keysToSnakeCase({ auroraDesignId }),
        }),
        transformResponse: (res: LinkAuroraDesignResponseT) => {
          SchemaValidator.validate<typeof LinkAuroraDesignResponseSchema>({
            schema: LinkAuroraDesignResponseSchema,
            schemaName: "LinkAuroraDesignResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        invalidatesTags: [PROJECT_TAGS.PROJECT_PROPOSALS],
        onQueryStarted: ({ projectId }, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(() => {
              dispatch(actions.invalidateProposalCache({ projectId }));
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.LINK_AURORA_DESIGN_SUCCESS,
                  severity: "success",
                }),
              );
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_LINKING_AURORA_DESIGN,
                  severity: "error",
                }),
              );
            });
        },
      }),
      refreshAuroraDesign: builder.mutation<RefreshAuroraDesignResponseT["body"], RefreshAuroraDesignParamsT>({
        query: ({ projectId, proposalId }) => ({
          url: `projects/${projectId}/proposals/${proposalId}/refresh_aurora_design`,
          method: "POST",
        }),
        transformResponse: (res: RefreshAuroraDesignResponseT) => {
          SchemaValidator.validate<typeof RefreshAuroraDesignResponseSchema>({
            schema: RefreshAuroraDesignResponseSchema,
            schemaName: "RefreshAuroraDesignResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        invalidatesTags: [PROJECT_TAGS.PROJECT_PROPOSALS],
        onQueryStarted: ({ projectId }, { dispatch, queryFulfilled }) => {
          queryFulfilled
            .then(() => {
              dispatch(actions.invalidateProposalCache({ projectId }));
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.REFRESH_AURORA_DESIGN_SUCCESS,
                  severity: "success",
                }),
              );
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_REFRESHING_AURORA_DESIGN,
                  severity: "error",
                }),
              );
            });
        },
      }),
      closeProject: builder.mutation<CloseProjectResponseT, CloseProjectParamsT>({
        query: ({ projectId, closeReasonId, closeNote }) => ({
          url: `provider_leads/${projectId}/close`,
          method: "POST",
          body: keysToSnakeCase({ closeReasonId, closeNote }),
        }),
        onQueryStarted: (_, { dispatch, queryFulfilled }) => {
          dispatch(
            actions.setAlertMessageVisible({
              message: "Updating...",
              severity: "info",
              duration: 6000,
            }),
          );
          queryFulfilled
            .then(({ data }) => {
              const projectDetails = camelizeKeys(data.body);
              dispatch(
                actions.updatePreSalesProject({ projectId: projectDetails.id, closeAt: projectDetails.closeAt }),
              );
              dispatch(actions.setProjectDetails({ providerLead: projectDetails }));
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.CLOSE_PROJECT_SUCCESS,
                }),
              );
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: messages.ERROR_CLOSING_PROJECT,
                  severity: "error",
                }),
              );
            });
        },
        invalidatesTags: [ORG_PROJECTS_TAGS.ORG_PRE_SALES_PROJECTS],
      }),
      getProjectActions: builder.query<GetProjectActionsResponseT, GetProjectActionsQueryParamsT>({
        query: ({ projectId }) => ({
          url: `provider_leads/${projectId}/project_actions`,
        }),
        transformResponse: (res: GetProjectActionsResponseT) => {
          SchemaValidator.validate<typeof GetProjectActionsResponseSchema>({
            schema: GetProjectActionsResponseSchema,
            schemaName: "GetProjectActionsResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res);
        },
        providesTags: [PROJECT_TAGS.PROJECT_ACTIONS],
      }),
      getPreSalesKpis: builder.query<GetPreSalesKpisResponseT, void>({
        queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
          const fetchPage = (pageNumber: number) =>
            baseQuery({ url: "/kpi/pre_sales", params: { page_number: pageNumber } });

          const firstResult = await fetchPage(1);
          if (firstResult.error) {
            return { error: firstResult.error };
          }

          const firstBody = (firstResult.data as PreSalesKpiApiResponseT)?.body;
          const totalPageCount = firstBody?.paging?.total_page_count ?? 1;

          const remainingPages = Array.from({ length: Math.max(0, totalPageCount - 1) }, (_, index) => index + 2);
          const remainingResults = await Promise.all(remainingPages.map(fetchPage));

          const dedupedByCode = new Map<string, PreSalesKpiT>();
          const collect = (body?: PreSalesKpiApiResponseT["body"]) => {
            (body?.kpis ?? []).forEach((kpi) => {
              if (kpi?.code && !dedupedByCode.has(kpi.code)) {
                dedupedByCode.set(kpi.code, { code: kpi.code, value: kpi.value ?? 0 });
              }
            });
          };

          collect(firstBody);
          const failedResult = remainingResults.find((result) => result.error);
          if (failedResult?.error) {
            return { error: failedResult.error };
          }
          remainingResults.forEach((result) => collect((result.data as PreSalesKpiApiResponseT)?.body));

          return { data: { kpis: Array.from(dedupedByCode.values()) } };
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled.catch(() => {
            dispatch(
              actions.setAlertMessageVisible({
                message: messages.ERROR_GETTING_KPI_DATA,
                severity: "error",
              }),
            );
          });
        },
        providesTags: [PROJECT_TAGS.PRE_SALES_KPIS],
      }),
      getContractsSignedCount: builder.query<number, void>({
        query: () => ({
          url: "/provider_leads",
          params: { tab: "post_sales", status: "active", limit: 1, paging_counts_only: true },
        }),
        transformResponse: (res: ProviderLeadsCountApiResponseT) => res.body?.paging?.item_count ?? 0,
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled.catch(() => {
            dispatch(
              actions.setAlertMessageVisible({
                message: messages.ERROR_GETTING_PROVIDER_LEADS_LIST,
                severity: "error",
              }),
            );
          });
        },
      }),
    }),
  });

export const {
  useCreatePowurlineOverrideMutation,
  useGetProjectProposalsQuery,
  useRenameProposalMutation,
  useGetProposalDetailsQuery,
  useRequestRevisionMutation,
  useRequestVisionRevisionMutation,
  useLinkAuroraDesignMutation,
  useRefreshAuroraDesignMutation,
  useCloseProjectMutation,
  useGetProjectActionsQuery,
  useGetPreSalesKpisQuery,
  useGetContractsSignedCountQuery,
} = providerLeadsApi;
